import 'satellite-polyfills';

import { createApp, type Component, type ComponentOptions } from 'vue';
import type { NamedValue } from 'vue-i18n';
import App from './App.vue';
import { createAppRouter } from './router';
import vuetify from './plugins/vuetify';
import i18n from './plugins/i18n';
import apiErrorsMixin from '@/components/mixins/apiErrorsMixin';
import VueTelInput from 'vue3-tel-input';
import Datepicker from '@vuepic/vue-datepicker';
import axiosClient from '@/api/axiosClient';
import { analyticsKey } from '@satellite/composables/useAnalytics';
import { createCometAnalyticsClient } from '@/analytics/client';

// Feature flags
import { plugin as featureFlagPlugin } from '@loadsmart/vue-feature-flags';
import { createFeatureFlagClient } from './plugins/featureFlagPlugin';

import store from './store';
import * as storeMixin from './store/mixin';

import initSentry from 'sentry-plugin';

import { setTranslator as setCoreTranslator } from '@nova/core';
import { setTranslator } from '@satellite/plugins/translator';

import { applyInitialCometLocale } from '@/lib/cometLocale';

import '@vuepic/vue-datepicker/dist/main.css';
import 'vue3-tel-input/dist/vue3-tel-input.css';

const app = createApp(App);

app.config.globalProperties.axiosClient = axiosClient;

app.provide(analyticsKey, createCometAnalyticsClient());

app.use(VueTelInput);
app.component('datepicker', Datepicker);

const sentryConfig = {
  sentry_dsn: import.meta.env.VITE_SENTRY_DSN,
  server_env: import.meta.env.MODE,
  nova_version: import.meta.env.PACKAGE_VERSION,
  neutron_url: import.meta.env.VITE_NEUTRON_URL,
  sentry_traces_sample_rate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 0
};

const components = import.meta.glob('./components/**/*.vue', { eager: true }) as Record<
  string,
  { default: Component }
>;
Object.entries(components).forEach(([path, definition]) => {
  // Get name of component, based on filename
  // "./components/Fruits.vue" will become "Fruits"
  const baseName = path.split('/').pop();
  if (!baseName) {
    return;
  }
  const componentName = baseName.replace(/\.\w+$/, '');

  // Register component on this Vue instance
  app.component(componentName, definition.default);
});

async function bootstrap() {
  const unleashClient = createFeatureFlagClient();
  app.use(featureFlagPlugin, { unleashClient, startClient: false });
  await unleashClient.start();
  const router = createAppRouter();

  initSentry(app, router, sentryConfig);

  app.use(router);
  app.use(vuetify);
  app.use(i18n);

  setTranslator(i18n.global.t, i18n.global.locale);
  setCoreTranslator((key: string, params?: NamedValue) => i18n.global.t(key, params));

  app
    .use(store)
    .mixin(storeMixin)
    .mixin(apiErrorsMixin as ComponentOptions);

  await router.isReady();
  try {
    await applyInitialCometLocale(router, i18n);
  } catch (error) {
    console.error('Failed to apply initial locale:', error);
  } finally {
    app.mount('#app');
  }
}

void bootstrap();
